<template>
  <ai-drawer
    v-model="drawerOpen"
    class="BookingRateConditionDrawer"
    overflow
    :title="$t('booking.rateCondition.title')"
    @popin-close="onPopinClose">
    <div class="BookingRateConditionDrawer-content">
      <ai-typo
        class="BookingRateConditionDrawer-title"
        as="h3"
        variant="heading-03">
        {{ title }}
      </ai-typo>

      <ai-typo
        class="BookingRateConditionDrawer-subTitle"
        as="h4"
        variant="heading-04"
        color="gold">
        {{ subtitle }}
      </ai-typo>

      <ai-typo as="span" variant="paragraph-04-regular">
        {{ description }}
      </ai-typo>

      <ai-separator :margin="3" />

      <div>
        <ai-typo
          class="BookingRateConditionDrawer-sectionTitle"
          as="h4"
          variant="paragraph-02-bold">
          {{ $t('booking.rateCondition.sectionTitle') }}
        </ai-typo>

        <div
          v-for="(policy, index) in policies"
          :key="`BookingRateCondition-item-${index}`"
          class="BookingRateConditionDrawer-conditionSection">
          <ai-typo
            class="BookingRateConditionDrawer-SubSectionTitle"
            as="span"
            variant="paragraph-04-bold">
            {{ policy?.label }}
          </ai-typo>

          <ai-typo as="span" variant="paragraph-04-regular">
            {{ policy?.description }}
          </ai-typo>
        </div>
      </div>

      <ai-separator :margin="3" />

      <div>
        <ai-typo
          class="BookingRateConditionDrawer-sectionTitle"
          as="h4"
          variant="paragraph-02-bold">
          {{
            $t('booking.rateCondition.totalTitle', {
              currency: bookingStore.currentCurrency,
            })
          }}
        </ai-typo>

        <div>
          <div class="BookingRateConditionDrawer-totalSectionRow">
            <ai-typo as="span" variant="paragraph-02-regular">
              {{ $t('booking.rateCondition.totalSubTitle') }}
            </ai-typo>
            <div v-if="memberPrice.amount">
              <ai-typo
                class="BookingRateConditionDrawer-totalRoomPrice"
                as="span"
                variant="paragraph-02-regular">
                {{ publicPrice.amount }}
              </ai-typo>
              <ai-typo as="span" variant="paragraph-02-bold">
                {{
                  $t('booking.rateCondition.memberPriceLabel', {
                    memberPrice: memberPrice.amount,
                  })
                }}
              </ai-typo>
            </div>
            <ai-typo
              v-else
              class="BookingRateConditionDrawer-totalPrice"
              as="span"
              variant="paragraph-02-bold">
              {{ publicPrice.amount }}
            </ai-typo>
          </div>

          <div
            v-for="(nightDetail, index) in nightsDetails"
            :key="`BookingRateCondition-price-${index}`"
            class="BookingRateConditionDrawer-totalSectionRow">
            <ai-typo as="span" variant="paragraph-04-bold">
              {{
                $t('booking.rateCondition.dateLabel', {
                  startDate: nightDetail.startDate,
                  endDate: nightDetail.endDate,
                })
              }}
            </ai-typo>
            <ai-typo
              v-if="nightDetail.price"
              as="span"
              variant="paragraph-04-regular">
              {{ nightDetail.price }}
            </ai-typo>
          </div>

          <ai-typo
            v-if="memberOffer"
            class="BookingRateConditionDrawer-condition"
            as="div"
            variant="paragraph-04-regular">
            {{ legalLabel }}
          </ai-typo>
        </div>

        <div>
          <div class="BookingRateConditionDrawer-totalSectionRow">
            <ai-typo as="span" variant="paragraph-02-regular">
              {{ mealPlanTitle }}
            </ai-typo>
            <ai-typo as="span" variant="paragraph-02-bold">
              {{ includedLabel }}
            </ai-typo>
          </div>

          <div
            v-for="(nightDetails, index) in nightsDetails"
            :key="`BookingRateCondition-breakfastDetail-${index}`"
            class="BookingRateConditionDrawer-totalSectionRow">
            <ai-typo as="span" variant="paragraph-04-bold">
              {{
                $t('booking.rateCondition.dateLabel', {
                  startDate: nightDetails.startDate,
                  endDate: nightDetails.endDate,
                })
              }}
            </ai-typo>
            <ai-typo as="span" variant="paragraph-04-regular">
              {{ includedLabel }}
            </ai-typo>
          </div>

          <!-- Included taxes -->
          <div
            v-if="taxesIncluded?.length && taxesIncluded?.length > 0"
            class="BookingRateConditionDrawer-conditionSection">
            <ai-typo
              class="BookingRateConditionDrawer-TaxeLabel"
              as="div"
              variant="paragraph-04-bold">
              {{ t('booking.rateCondition.taxIncludedLabel') }}
            </ai-typo>
            <div>
              <div
                v-for="(taxDetail, index) in taxesIncluded"
                :key="`BookingRateCondition-taxDetail-included-${index}`"
                class="BookingRateCondition-taxDetails">
                <ai-typo as="span" variant="paragraph-04-regular">
                  {{ taxDetail }}
                </ai-typo>
              </div>
            </div>
          </div>

          <!-- Excluded taxes -->
          <div
            v-if="taxesExcluded?.length && taxesExcluded.length > 0"
            class="BookingRateConditionDrawer-conditionSection">
            <ai-typo
              class="BookingRateConditionDrawer-TaxeLabel"
              as="div"
              variant="paragraph-04-bold">
              {{ t('booking.rateCondition.taxNotIncludedLabel') }}
            </ai-typo>
            <div>
              <div
                v-for="(taxDetail, index) in taxesExcluded"
                :key="`BookingRateCondition-taxDetail-excluded-${index}`"
                class="BookingRateCondition-taxDetails">
                <ai-typo as="span" variant="paragraph-04-regular">
                  {{ taxDetail }}
                </ai-typo>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ai-drawer>
</template>

<script setup lang="ts">
import { useOffer, useOfferToDrawer } from './useDrawerOffer';

import { useBookingStore } from '~~/domains/booking/store';
import { useUserStore } from '~~/domains/user';
import { useLayoutStore } from '~~/domains/ux';

type Props = {
  modelValue: boolean;
  productCode: string;
  adults?: number;
  childrenAges?: number[];
  dateIn?: string;
  hotelCode?: string;
  nights?: number;
  offerCode?: string;
  allTaxesIncluded?: boolean;
  focusOnCloseElement?: HTMLElement;
};

type Emits = {
  (event: 'update:modelValue', value: boolean): void;
};

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const layoutStore = useLayoutStore();
const bookingStore = useBookingStore();

const queryVariables = computed(() => ({
  adults: props.adults ?? 2,
  childrenAges: props.childrenAges ?? [],
  dateIn: props.dateIn ?? '',
  hotelCode: props.hotelCode ?? '',
  nights: props.nights ?? 1,
  offerCode: props.offerCode ?? '',
  productCode: props.productCode,
}));

const { loading, publicOffer, memberOffer, offerDetails } =
  useOffer(queryVariables);

const areAllTaxesIncluded = useAreAllTaxesIncluded();

const {
  title,
  subtitle,
  description,
  policies,
  memberPrice,
  publicPrice,
  nightsDetails,
  mealPlanTitle,
  mealPlanIncluded,
  taxesIncluded,
  taxesExcluded,
} = useOfferToDrawer(
  {
    publicOffer,
    memberOffer,
    offerDetails,
  },
  {
    areAllTaxesIncluded,
  },
);

const drawerOpen = computed({
  get() {
    return props.modelValue && !loading.value;
  },
  set(value: boolean) {
    emit('update:modelValue', value);
  },
});

const { t } = useI18n();
const userStore = useUserStore();

const logged = computed(() => userStore.isLogged);
const status = computed(() => userStore.loyaltyStatus);

const legalLabel = computed<string>(() =>
  logged.value && memberOffer.value
    ? t('booking.rateCondition.legalConditionLogged', { status: status.value })
    : t('booking.rateCondition.legalCondition'),
);

const includedLabel = computed<string>(() =>
  mealPlanIncluded.value
    ? t('booking.rateCondition.includedLabel')
    : t('booking.rateCondition.notIncludedLabel'),
);

const onPopinClose = () => {
  props.focusOnCloseElement?.focus();
};

watch(
  () => drawerOpen.value,
  isDrawerOpen => {
    layoutStore.setScrollLocked(isDrawerOpen);
  },
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';

.BookingRateConditionDrawer-content {
  padding: constants.$padding-02 constants.$padding-03;
}

.BookingRateConditionDrawer-title {
  margin-bottom: constants.$inner-03;
}

.BookingRateConditionDrawer-subTitle {
  margin-bottom: constants.$inner-01;
}

.BookingRateConditionDrawer-sectionTitle {
  margin-bottom: constants.$margin-01;
}
.BookingRateConditionDrawer-conditionSection {
  display: flex;
  flex-direction: column;

  margin-bottom: constants.$margin-02;
}

.BookingRateConditionDrawer-SubSectionTitle {
  margin-bottom: constants.$inner-01;

  &::first-letter {
    text-transform: capitalize;
  }
}

.BookingRateConditionDrawer-totalSectionRow {
  display: flex;
  justify-content: space-between;

  margin-bottom: constants.$inner-02;

  &:last-child {
    margin-bottom: constants.$margin-02;
  }
}

.BookingRateConditionDrawer-totalRoomPrice {
  text-decoration: line-through;
  margin-right: constants.$inner-02;
}

.BookingRateConditionDrawer-condition {
  margin-bottom: constants.$margin-02;
  gap: constants.$inner-01;
}

.BookingRateConditionDrawer-TaxeLabel {
  margin-top: constants.$margin-02;
  margin-bottom: constants.$inner-01;
}
.BookingRateCondition-taxDetails {
  display: flex;
  flex-direction: column;
}
</style>
